import { createElement } from '../../../core/dom/helpers.js';
import { createOptionsProvider, subscribeWithInitial } from '../../../core/options/options-provider.js';
import { rebuildSelectOptions } from '../../../core/options/rebuild-select-options.js';

/** @type {import('../../../contracts/filters.js').FilterDriver} */
export const selectFilterDriver = {
  type: 'select',
  mount(container, ctx) {
    const { def, onChange } = ctx;
    const placeholder = def.placeholder ?? 'All';

    const select = /** @type {HTMLSelectElement} */ (createElement('select', {
      id: `filter-${def.key}`,
      name: def.key,
    }));
    container.appendChild(select);

    if (def.loadOptions) {
      // --- Async path ---
      let destroyed = false;
      let currentValue = ctx.value != null ? String(ctx.value) : '';
      let initialRender = true;
      /** @type {ReadonlyArray<import('../../../contracts/forms.js').FieldOption> | null} */
      let prevOptions = null;

      const provider = createOptionsProvider({
        options: def.options,
        loadOptions: def.loadOptions,
        mapOption: def.mapOption,
      });

      const unsubscribe = subscribeWithInitial(provider, (state) => {
        if (destroyed) return;

        select.disabled = state.loading;

        if (state.options !== prevOptions) {
          prevOptions = state.options;

          if (initialRender) {
            rebuildSelectOptions(select, state.options, placeholder, currentValue);
            initialRender = false;
          } else {
            const valueLost = rebuildSelectOptions(select, state.options, placeholder, currentValue);
            if (valueLost) {
              currentValue = '';
              onChange(currentValue);
            }
          }
        }
      });

      select.addEventListener('change', () => {
        currentValue = select.value;
        onChange(currentValue);
      });

      provider.load('');

      return {
        setValue(value) {
          currentValue = value != null ? String(value) : '';
          select.value = currentValue;
        },
        getValue() {
          return currentValue;
        },
        destroy() {
          destroyed = true;
          unsubscribe();
          provider.destroy();
          select.remove();
        },
      };
    }

    // --- Static path (backward compatible) ---
    select.appendChild(createElement('option', { value: '' }, [placeholder]));
    for (const opt of def.options ?? []) {
      const option = createElement('option', { value: opt.value }, [opt.label]);
      if (ctx.value === opt.value) {
        option.setAttribute('selected', 'selected');
      }
      select.appendChild(option);
    }

    select.addEventListener('change', () => onChange(select.value));

    return {
      setValue(value) {
        select.value = typeof value === 'string' ? value : '';
      },
      getValue() {
        return select.value;
      },
      destroy() {
        select.remove();
      },
    };
  },
};
